import React, { useState, useRef, useEffect } from 'react'
import { LinearProgress, Typography, Dialog, IconButton, Box } from '@mui/material'
import { PollMultichoice } from './Components/PollMultiChoice'
import { PollItemF, PollF, PollComponentProps } from './Components/PollComponentType'
import { useDataProvider, useNotify } from 'react-admin'
import { LxLoadingSpinner } from '@src/components/loader/loadingSpinner'
import styles from './Poll.module.scss'
import { useUserContext } from '@logic/contexts/AppStore/UserContext.tsx'
import { PollPage } from './Components/PollPage'
import { useNavigate } from 'react-router-dom'
import { PollVideo } from './Components/PollVideo'
import { PollStatement } from './Components/PollStatement'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
interface PollProps {
  pollData: PollF
  pollScope: string
  resourceId: string
  pollId: string
  backToPath: string
}

interface ComponentMap {
  [key: string]: React.FunctionComponent<PollComponentProps>
}

const uiComponentsMap: ComponentMap = {
  statement: PollStatement,
  video: PollVideo,
  page: PollPage,
  multi_choice: PollMultichoice,
}

export const normalizeOptional = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') return value.toLowerCase() === 'true'
  return false
}

const Poll: React.FC<PollProps> = ({ pollData, pollScope, resourceId, pollId, backToPath }) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const { refreshUser } = useUserContext()

  const showProgress = !pollData.uiProperties?.hideProgress

  const showQuestionNumber = pollData.uiProperties?.showQuestionNumber
  const questionTime = useRef<string>(new Date().toISOString())
  const [currentPollItem, setCurrentPollItem] = useState<PollItemF | null>(null)
  const UIComponent = currentPollItem ? uiComponentsMap[currentPollItem.properties.type] : null
  const [progress, setProgress] = useState(0.0)
  const [progressTime, setProgressTime] = useState<number>(0)
  const title = currentPollItem?.properties.title || null

  const [isLoading, setIsLoading] = useState(false)

  const questionHistory = useRef<PollItemF[]>([pollData.question])
  const questionIndex = useRef<number>(0)

  const navigate = useNavigate()

  useEffect(() => {
    if (!pollData) return
    const { question, duration, lastQuestion, nQuestions } = pollData
    setCurrentPollItem(question)
    const nQ = parseInt(nQuestions, 10)
    const lastQ = parseInt(lastQuestion ?? 0, 10)
    const dur = parseInt(duration, 10)
    setProgress((lastQ / nQ) * 100)
    const remaining = nQ - lastQ
    setProgressTime(Math.round((remaining * dur) / nQ / 60))
  }, [pollData])

  const onPollresponse = async (response: { skipped: boolean; answer: Record<string, any> }) => {
    setIsLoading(true)
    const questionResp = {
      id: currentPollItem?.id,
      question_time: questionTime.current,
      answer_time: new Date().toISOString(),
      answer: response.answer,
      skipped: response.skipped ? 'true' : 'false',
    }
    try {
      const { data }: { data: PollF } = await dataProvider.update('Poll', {
        id: resourceId,
        data: {
          poll_id: pollId,
          scope: pollScope,
          question_resp: questionResp,
        },
        previousData: null,
      })

      if (data?.action?.success === false) {
        notify(data.action.message || 'Something went wrong', { type: 'warning' })
      }

      const question = data?.question
      if (!question) {
        if (pollScope === 'USER') {
          await refreshUser()
        }
        navigate(backToPath)
        return
      }
      setCurrentPollItem(question)

      if (question.id && pollData.nQuestions) {
        const progress = (Number(question.id) / Number(pollData.nQuestions)) * 100
        setProgress(progress)
      }

      updateQuestionHistory(question)
    } catch (error: any) {
      const errorMessage = error?.message || 'Something went wrong'
      notify(errorMessage, { type: 'warning' })
    } finally {
      setIsLoading(false)
    }
  }

  const getPrevQuestion = () => {
    if (questionIndex.current >= 0) {
      questionHistory.current.splice(questionIndex.current, 1)
      questionIndex.current--
      const question = questionHistory.current[questionIndex.current]
      if (!question) {
        return
      }
      setProgress((parseInt(question.id) / parseInt(pollData.nQuestions)) * 100)
      setCurrentPollItem(question)
    }
  }

  const updateQuestionHistory = (question: PollItemF) => {
    if (!question) {
      return
    }
    questionHistory.current.push(question)
    questionIndex.current = questionHistory.current.length - 1
  }

  return (
    <Dialog
      open
      role='dialog'
      aria-labelledby='poll-dialog-title'
      maxWidth='lg'
      fullWidth
      aria-label='Poll Questions'
      sx={{
        '& .MuiDialog-paper': {
          aspectRatio: {
            xs: 'unset',
            sm: 'unset', 
            md: '4 / 3', 
          },
          maxHeight: {
            xs: '95vh',
            sm: '90vh', 
            md: '80vh', 
          },
          height: {
            xs: '95vh', 
            sm: '90vh',
            md: 'auto', 
          },
          margin: {
            xs: '8px',
            sm: '16px',
            md: 'auto',
          },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      {isLoading && (
        <div className={styles.loadingOverlay}>
          <LxLoadingSpinner />
        </div>
      )}
      {(title || showProgress) && (
        <div className={styles.PollHeader}>
          {title && <h3 id='poll-dialog-title'>{title}</h3>}

          {showProgress && (
            <div className={styles.progressContainer}>
              <div className={styles.previousButton}>
                <IconButton aria-label='previous-question' disabled={questionIndex.current <= 0} onClick={getPrevQuestion}>
                  <ArrowBackIcon />
                </IconButton>
              </div>

              {showQuestionNumber && (
                <Typography variant='body1' className={styles.progressText}>
                  Question {currentPollItem?.id} of {pollData.nQuestions} ({Math.floor(progress)}%)
                </Typography>
              )}

              <LinearProgress
                variant='determinate'
                value={progress}
                className={styles.progressBar}
                aria-label='Poll Progress'
                aria-valuenow={progress}
                sx={{ height: 10, borderRadius: 16, backgroundColor: '#94ccec' }}
              />

              <Typography variant='body1' className={styles.progressText}>
                {progressTime} min
              </Typography>
            </div>
          )}
        </div>
      )}

      <div className={styles.dialogContent}>
        <div className={styles.UIComponentWrapper}>{UIComponent ? <UIComponent data={currentPollItem!} responseCallback={onPollresponse} /> : <LxLoadingSpinner />}</div>
      </div>
    </Dialog>
  )
}

export default Poll
